import {Component, HostListener, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router} from "@angular/router";
import {CardsByQrManageRatingNavigationExtrasModel} from "./cards-by-qr-manage-rating-navigation-extras.model";
import {CardsByQrRatingComponent} from "../cards-by-qr-rating.component";
import {firstValueFrom} from "rxjs";
import {UserService} from "../../../service/user.service";
import {DEFAULT_USER_ICON} from "../../../constants/global.constants";
import {data} from "jquery";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {UserCardReviewService} from "../../../service/user-card-review.service";
import {HttpErrorResponse} from "@angular/common/http";
import {UserCardReviewRequest} from "../../../models/userCardReviewRequest";
import {SnackBarUtil} from "../../../qr-copy-message/snack-bar-util.service";
import {MockupDataModel} from "../../../qr-copy-message/mockup-data.model";
import {CardsByQrRatingCommonService} from "../cards-by-qr-rating-common.service";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-cards-by-qr-manage-rating',
  templateUrl: './cards-by-qr-manage-rating.component.html',
  styleUrls: ['./cards-by-qr-manage-rating.component.scss', '../cards-by-qr-rating.component.scss',
    '../../../layouts/filled-form-field/filled-form-field.component.scss']
})
export class CardsByQrManageRatingComponent implements OnInit {

  protected dataFromReviewsPage!: CardsByQrManageRatingNavigationExtrasModel;
  protected currentUserProfilePicture: Blob | string = DEFAULT_USER_ICON;
  protected userCardProfilePicture: string | null = null;

  protected manageForm!: FormGroup;
  protected autosizeMinRows = 5;

  private isSaveButtonClicked: boolean = false;
  private qrCodeFromParams!: string;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly userService: UserService,
    private readonly fb: FormBuilder,
    private readonly userCardReviewService: UserCardReviewService,
    private readonly snackBar: SnackBarUtil,
    private readonly cardsByQrRatingCommonService: CardsByQrRatingCommonService,
    private readonly translate: TranslateService,
  ) {
    this.initDataFromRouter().catch(_ => {
      this.router.navigate(['/error-page']).catch();
    });
  }

  ngOnInit() {
    this.initUserCardPictures(this.dataFromReviewsPage.currentUser.id);
    this.manageForm = this.createManageForm();
    this.loadReviewDataIfActionIsEdit();
    this.setAutosizeRows();
    this.initUserCardProfilePicture();
  }

  @HostListener('window:resize')
  onResize() {
    this.setAutosizeRows();
  }

  private setAutosizeRows(): void {
    this.autosizeMinRows = window.innerHeight < 700 ? 3 : 5;
  }

  get currentUserFullName(): string {
    return this.dataFromReviewsPage.currentUser.name + ' ' + this.dataFromReviewsPage.currentUser.surname;
  }

  get reviewTextControl() {
    return this.manageForm.get('reviewText');
  }

  get isSaveButtonDisabled(): boolean {
    return this.isSaveButtonClicked || this.manageForm.invalid;
  }

  get isEditAction(): boolean {
    return !!(this.dataFromReviewsPage.review);
  }

  get rating(): number {
    return this.manageForm.get('rating')?.value || 0;
  }

  protected submitManageForm(): void {
    if (this.isSaveButtonDisabled) {
      return;
    }
    this.isSaveButtonClicked = true;
    const request: UserCardReviewRequest = this.manageForm.getRawValue() as UserCardReviewRequest;
    request.reviewText = request.reviewText.trim();
    if (this.isEditAction) {
      this.editReview(request);
    } else {
      this.createReview(request);
    }
  }

  private createReview(request: UserCardReviewRequest) {
    this.userCardReviewService.create(request).subscribe({
      next: _ => {
        this.redirectWithSuccessMessage(this.translate.instant('rating.successMessage'));
      },
      error: (error: HttpErrorResponse) => {
        if (error.error.message.includes("already reviewed this card")) {
          this.handleReviewSubmissionError(error, this.translate.instant('rating.alreadyReviewedError'));
        }
        this.handleReviewSubmissionError(error, this.translate.instant('rating.genericError'));
      }
    })
  }

  private editReview(request: UserCardReviewRequest) {
    this.userCardReviewService.update(request).subscribe({
      next: _ => {
        this.redirectWithSuccessMessage(this.translate.instant('rating.updated_review_message'))
      },
      error: (error: HttpErrorResponse) => {
        this.handleReviewSubmissionError(error, this.translate.instant('rating.updated_review_failed_message'));
      }
    })
  }

  private handleReviewSubmissionError(error: HttpErrorResponse, message: string) {
    const data = MockupDataModel.error(message);
    this.showSnackbar(data, SnackBarUtil.CLASSES.error);
    this.isSaveButtonClicked = false;
    throw new Error("Failed to create review: " + error.error.message);
  }

  private redirectWithSuccessMessage(message: string) {
    this.router.navigate(
      [`/${this.qrCodeFromParams}/rating`],
      {replaceUrl: true},
    ).then(() => {
      this.isSaveButtonClicked = false;
      const data = MockupDataModel.success(message);
      this.showSnackbar(data, SnackBarUtil.CLASSES.success);
    });
  }

  protected deleteReview(): void {
    if (!this.isEditAction) {
      return;
    }
    const reviewId: bigint = this.dataFromReviewsPage.review!.id;
    this.cardsByQrRatingCommonService.onClickDeleteReview(() => {
      this.deleteReviewById(reviewId);
      this.redirectWithSuccessMessage("Your review has been deleted successfully.");
    });
  }

  protected onCancelClick(): void {
    this.backToCardReviews();
  }

  protected backToCardReviews(): void {
    this.router.navigate([`/${this.qrCodeFromParams}/rating`]).catch();
  }

  protected setRating(newRating: number): void {
    this.manageForm.get('rating')?.setValue(newRating);
  }

  private async initDataFromRouter(): Promise<void> {
    this.getDataFromNavigationExtras();
    await this.getCardUsernameFromParams();
    this.verifyDataFromRouter();
  }

  private verifyDataFromRouter() {
    if (!this.dataFromReviewsPage && this.qrCodeFromParams) {
      this.router.navigate([`/${this.qrCodeFromParams}/rating`]).catch();
    } else if (!this.dataFromReviewsPage || !this.qrCodeFromParams) {
      this.router.navigate(['/error-page']).catch();
      throw new Error('Data from reviews page is not available or QR code is missing.');
    }
  }

  private getDataFromNavigationExtras() {
    const stateData: CardsByQrManageRatingNavigationExtrasModel | null | undefined =
      this.router.getCurrentNavigation()?.extras.state?.['data'];
    this.dataFromReviewsPage = stateData!;
  }

  private async getCardUsernameFromParams() {
    const params: Params = await firstValueFrom(this.route.params);
    this.qrCodeFromParams = params[CardsByQrRatingComponent.QR_CODE_PARAM];
    if (!this.qrCodeFromParams) {
      this.router.navigate(['/error-page']).catch();
      throw new Error('QR code parameter is missing in the route.');
    }
  }

  private initUserCardPictures(id: bigint) {
    this.getCurrentUserProfilePictureFromStorage(Number(id));
  }

  private initUserCardProfilePicture() {
    const picture = this.dataFromReviewsPage.userCardProfilePicture;
    this.userCardProfilePicture = (picture && picture !== 'null' && picture.trim() !== '')
      ? picture
      : DEFAULT_USER_ICON;
  }

  private getCurrentUserProfilePictureFromStorage(id: number) {
    this.userService.getCurrentProfileImageUrl().subscribe({
      next: (imageData) => {
        if (imageData) {
          this.currentUserProfilePicture = imageData;
        } else {
          this.getCurrentUserImageFromAPI(id);
        }
      },
      error: _ => {
        this.getCurrentUserImageFromAPI(id);
      }
    })
  }

  private getCurrentUserImageFromAPI(id: number) {
    this.userService.getProfileImage(BigInt(id)).subscribe({
      next: (imageData) => {
        const fileReader = new FileReader();
        fileReader.onload = (event) => {
          this.currentUserProfilePicture = event.target?.result as string;
        };
        fileReader.readAsDataURL(imageData);
      },
      error: _ => {
        this.currentUserProfilePicture = DEFAULT_USER_ICON;
      }
    });
  }

  private deleteReviewById(reviewId: bigint): void {
    this.userCardReviewService.deleteReviewById(reviewId).subscribe({
      next: () => {
        this.router.navigate([`/${this.qrCodeFromParams}/rating`]).then(() => {
          this.showSnackbar(MockupDataModel.success('Your review has been deleted.'), SnackBarUtil.CLASSES.success);
        })
      },
      error: (err: HttpErrorResponse) => {
        this.showSnackbar(MockupDataModel.error('Cannot delete your review. Please try again later.'), SnackBarUtil.CLASSES.error);
        throw new Error('Error deleting review: ' + (err.error.message || 'Unknown error'));
      }
    })
  }

  private createManageForm(): FormGroup {
    return this.fb.group({
      id: [0],
      rating: [0, [Validators.required, Validators.min(1), Validators.max(5)]],
      reviewText: ['', [Validators.maxLength(1000)]],
      userCardId: [this.dataFromReviewsPage.userCardId],
    });
  }

  private loadReviewDataIfActionIsEdit() {
    if (this.isEditAction) {
      this.manageForm.patchValue({
        id: this.dataFromReviewsPage.review!.id,
        rating: this.dataFromReviewsPage.review!.rating,
        reviewText: this.dataFromReviewsPage.review!.reviewText,
        userCardId: this.dataFromReviewsPage.userCardId
      });
    }
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data
    });
  }

  protected onProfileImageError(): void {
    this.currentUserProfilePicture = DEFAULT_USER_ICON;
  }

  protected readonly data = data;
}
